/* eslint-disable */
import {
    ArrowDown, ArrowUp, ArrowUpDown, Download, Eye, SearchIcon
} from 'lucide-react';
import React, {
    useCallback, useEffect, useMemo, useState
} from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Input, InputGroup } from 'rsuite';
import { urls } from '@/Constants';
import { inputHelper, keyPressHandler } from '@/Helpers';
import { tabs } from './tabs.constants';
import GrnHeader from '../GRNPage/grnHeader';
import SortComp from './SortComp';
import TableWithTabs from './TableWithTabs';
import Info from '@/Components/InfoTab/Info';
import useKeyPress from '@/Helpers/useKeyPress';
import moment from 'moment';
import { getTextColor, getTooltipTitle, useSessionStorageList } from './helper';
import { Tooltip } from '@mui/material';

const SORT_OPTIONS = [
    { value: 'ingredientName', label: 'Ingredient Name' },
    { value: 'orderedWeight', label: 'Ordered Weight' },
    { value: 'issuedWeight', label: 'Issued Weight' },
    { value: 'issuing%', label: 'Issuing %' },
];

const SORT_COST_OPTIONS = [
    { value: 'ingredientName', label: 'Ingredient Name' },
    { value: 'orderedCost', label: 'Ordered Cost' },
    { value: 'issuedCost', label: 'Issued Cost' },
    { value: 'variance', label: 'Variance' },
];

const WEIGHT_BASED_COLUMNS = [
    { title: 'Client Variant ID', key: 'clientVariantId', fixed: true },
    { title: 'Ingredient Name', key: 'ingredientName', fixed: true },
    { title: 'Price/Unit', key: 'price' },
    { title: 'Ordered Weight', key: 'orderedWeight' },
    { title: 'Issued Weight', key: 'issuedWeight' },
    { title: 'Issuing %', key: 'issuing%' },
    { title: 'View', key: 'view' },
];

const COST_BASED_COLUMNS = [
    { title: 'Client Variant ID', key: 'clientVariantId', fixed: true },
    { title: 'Ingredient Name', key: 'ingredientName', fixed: true },
    { title: 'Price/Unit', key: 'price' },
    { title: 'Ordered Cost', key: 'orderCost' },
    { title: 'Issued Cost', key: 'issuedCost' },
    { title: 'Variance', key: 'variance' },
    { title: 'View', key: 'view' },
];

const FIELD_MAP = {
    UyId: 'podId',
    Batch: 'meal',
    'Issued Date': 'scheduledAt',
    'Total Issued Percentage': 'fullfilledPercentage',
    'Total Weight (Issued/Ordered)': 'totalWeight',
    'Total Cost (Issued/Ordered)': 'totalCost',
    'Ideal Cost': 'idealCost',
};

const TABLE_TAB_CONFIG = {
    tabList: [{
        id: 1,
        value: 'ingredient',
        label: 'Ingredient',
    }],
    activeTabColor: 'bg-yellow-200',
};

const extractNumeric = (text) => {
    if (!text) return NaN;
    const num = parseFloat(String(text).replace(/[^\d.-]/g, ''));
    return isNaN(num) ? NaN : num;
};

const getTextFromElement = (element) => {
    if (!React.isValidElement(element)) return element;
    const { children } = element.props;

    if (Array.isArray(children)) {
        return children.map(getTextFromElement).join('');
    }
    if (React.isValidElement(children)) {
        return getTextFromElement(children);
    }
    return children ?? '';
};

const formatCurrency = (value) => `₹${parseFloat(value)?.toFixed(2)}`;
const formatQuantity = (value, unit) => `${parseFloat(value)?.toFixed(2)} ${unit}`;

const useProductData = (list, name) => {
    const [idx, setIdx] = useState(-1);
    const [productData, setProductData] = useState({});

    useEffect(() => {
        if (list?.length > 0) {
            const index = list?.findIndex((item) => item?.headerInfo?.podName === name);
            const obj = index !== -1 ? list[index] : null;
            setProductData(obj);
            setIdx(index);
        }
    }, [list, name]);

    return { productData, idx };
};

function IssuedDetailComp({ name, filters, meal, rowIndex }) {
    const updatedFilters = {
        ...filters,
        dateRange: {
          ...filters?.dateRange,
          from: new Date(new Date(filters?.dateRange?.from)?.setDate(
            new Date(filters?.dateRange?.from)?.getDate() - 1
          ))?.toISOString(),
        },
      };

    const [uiState, setUiState] = useState({
        selectedTab: tabs[0]?.id,
        tableTab: 'ingredient',
        search: '',
        isSortOpen: false,
        sortVal: {},
    });

    const list = useSessionStorageList();
    const { productData, idx } = useProductData(list, name);
    const keyPressed = useKeyPress();
    const history = useHistory();
    const backUrl = useMemo(() => `${urls.EXTENSION}${urls.AI_FMS_PAGE}?parentId=1&subTab=ISSUED&name=${name}&mealType=${meal}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPodFilter=${filters?.fromPodFilter}&rowIndex=${idx}`);
    const productInfo = useMemo(() => {
        const headerInfo = productData?.headerInfo;
        if (!headerInfo) return {};

        return {
            podId: headerInfo?.podId,
            podName: headerInfo?.podName,
            scheduledAt: headerInfo?.scheduledAt,
            meal: meal,
            fullfilledPercentage: `${headerInfo?.fullfilledPercentage}%`,
            totalWeight: headerInfo?.totalWeight,
            totalCost: headerInfo?.totalCost,
            idealCost: headerInfo?.idealCost,
        };
    }, [productData, meal]);

    const updateUiState = useCallback((updates) => {
        setUiState(prev => ({ ...prev, ...updates }));
    }, []);

    const handleSortChange = useCallback((value, type) => {
        setUiState({
            ...uiState,
            sortVal: {
                sortColumnName: value,
                sortType: type,
            },
        });
    }, [uiState?.selectedTab]);


    const navigateQc = useCallback((e) => {
        const { dataset } = inputHelper(e);
        let newListItem = null;
        
        if (dataset.type === 'next') {
            newListItem = list?.[idx + 1];
        } else {
            newListItem = list?.[idx - 1];
        }

        if (newListItem) {
            history.push({
                pathname: `${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}`,
                search: `mode=issued&name=${newListItem?.headerInfo?.podName}&meal=${meal}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${idx}`,
            });
        }
    }, [list, idx, history, filters]);

    const handleExport = useCallback(() => {
        const isWeightTab = uiState.selectedTab === 'weight';
        const headers = isWeightTab ? [
            'Meal Name', 'Export Type', 'Client Variant ID', 'Ingredient Name', 'Price/Unit', 'Ordered Weight', 'Issued Weight', 'Issuing %',
        ] : [
            'Meal Name', 'Export Type', 'Client Variant ID', 'Ingredient Name', 'Price/Unit', 'Ordered Cost', 'Issued Cost', 'Variance',
        ];

        const data = productData?.list?.map((item) => {
            const baseData = [
                meal ?? '-',
                'Issued Summary',
                item?.itemClientVariantId,
                item?.ingName,
            ];

            if (isWeightTab) {
                return [
                    ...baseData,
                    item?.ingData?.map(ing => `${ing?.rate}/${ing?.unit}`).join('; '),
                    item?.ingData?.map(ing => formatQuantity(ing?.orderQty, ing?.unit)).join('; '),
                    item?.ingData?.map(ing => formatQuantity(ing?.fullfilledQty, ing?.unit)).join('; '),
                    item?.ingData?.map(ing => `${ing?.fullfilledPercentage}%`).join('; '),
                ];
            } else {
                return [
                    ...baseData,
                    item?.ingData?.map(ing => `${ing?.rate}/${ing?.unit}`).join('; '),
                    item?.ingData?.map(ing => ing?.orderQty * ing?.rate).join('; '),
                    item?.ingData?.map(ing => ing?.fullfilledCost).join('; '),
                    item?.ingData?.map(ing => {
                        const orderCost = ing?.orderQty * ing?.rate;
                        const diff = Math.abs(orderCost - ing?.fullfilledCost);
                        return orderCost > ing?.fullfilledCost ? `↓${diff.toFixed(0)}` : `↑${diff.toFixed(0)}`;
                    }).join('; '),
                ];
            }
        }) || [];

        const csvData = [headers, ...data];
        const csvContent = csvData.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${productData?.headerInfo?.podName}_issued_${filters?.dateRange ? moment(filters?.dateRange?.from).format('DD-MM-YYYY') : ''}-${filters?.dateRange ? moment(filters?.dateRange?.to).format('DD-MM-YYYY') : ''}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }, [uiState.selectedTab, productData, meal, filters]);

    const buttons = useMemo(() => [
        {
            label: 'Export',
            icon: <Download size={16} />,
            handleClick: handleExport,
            noLevelSpecific: true,
            style: 'bg-teal-600 text-white',
        },
    ], [handleExport]);

    useEffect(() => {
        const newListItem = list?.[idx] ?? null;
        if (
            (keyPressed === 'ArrowLeft' || keyPressed === 'ArrowRight') &&
            newListItem !== null
        ) {
            const targetItem = keyPressHandler(keyPressed, list, idx);
            if (targetItem) {
                history.push({
                    pathname: `${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}`,
                    search: `mode=issued&name=${targetItem?.headerInfo?.podName}&meal=${meal}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${idx}`,
                });
            }
        }
    }, [keyPressed]);

    const generatedData = useCallback(
        (list, customRowData = {}) => {
            const { sortColumnName: sortCol, sortType } = uiState?.sortVal;

            let result = list ? [...list] : [];

            if (sortCol && sortType && customRowData[sortCol]) {
                const renderFn = customRowData[sortCol];
                result.sort((a, b) => {
                    const aVal = getTextFromElement(renderFn(a));
                    const bVal = getTextFromElement(renderFn(b));

                    const numA = extractNumeric(aVal);
                    const numB = extractNumeric(bVal);

                    if (!isNaN(numA) && !isNaN(numB)) {
                        return sortType === 'ascending' ? numA - numB : numB - numA;
                    }

                    return sortType === 'ascending'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal));
                });
            }

            const searchTerm = uiState.search?.toLowerCase()?.trim();
            if (searchTerm) {
                result = result.filter(
                    (item) => item?.ingName?.toLowerCase()?.trim()?.includes(searchTerm) ||
                        item?.itemClientVariantId?.toLowerCase()?.trim()?.includes(searchTerm)
                );
            }

            return result;
        },
        [uiState.search, uiState?.sortVal]
    );

    const createRowRenderer = useCallback((field, formatter) => (product) => (
        <span className='text-[12px]'>{formatter(product)}</span>
    ), []);

    const createIngDataRenderer = useCallback((formatter) => (product) => (
        <div className='flex flex-col gap-2'>
            {product?.ingData?.map((item, index) => (
                <span key={index} className='text-[12px]'>
                    {formatter(item)}
                </span>
            ))}
        </div>
    ), []);

    const getWeightBasedTable = useMemo(() => {
        const customRowData = {
            clientVariantId: createRowRenderer('clientVariantId', (product) => product?.itemClientVariantId),
            ingredientName: createRowRenderer('ingredientName', (product) => product?.ingName),
            price: createIngDataRenderer((item) => `${formatCurrency(item?.rate)}/${item?.unit}`),
            orderedWeight: createIngDataRenderer((item) => formatQuantity(item?.orderQty, item?.unit)),
            issuedWeight: createIngDataRenderer((item) => formatQuantity(item?.fullfilledQty, item?.unit)),
            'issuing%': createIngDataRenderer((item) => (
                <Tooltip title={getTooltipTitle(item?.fullfilledQty, item?.orderQty, 'issued')}>
                    <span className={`text-[12px] ${getTextColor(item?.fullfilledQty, item?.orderQty)}`}>{`${item?.fullfilledPercentage}%`}</span>
                </Tooltip>
            )),
            view: (product) => (
                <div className='flex flex-col gap-2'>
                    {product?.ingData?.map((item, index) => (
                        <Eye
                            key={index}
                            className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125'
                            onClick={() => window.open(
                                `${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${encodeURIComponent(item?.recipeName)}&mealType=${meal}&dateRange=${encodeURIComponent(
                                    JSON.stringify(updatedFilters?.dateRange)
                                )}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({id: -999}))}`
                            )}
                        />
                    ))}
                </div>
            ),
        };

        return {
            data: generatedData(productData?.list || [], customRowData),
            tableRows: customRowData,
            tableColumns: WEIGHT_BASED_COLUMNS,
        };
    }, [productData?.list, generatedData, createRowRenderer, createIngDataRenderer, meal, filters]);

    const getCostBasedTable = useMemo(() => {
        const customRowData = {
            clientVariantId: createRowRenderer('clientVariantId', (product) => product?.itemClientVariantId),
            ingredientName: createRowRenderer('ingredientName', (product) => product?.ingName),
            price: createIngDataRenderer((item) => `${formatCurrency(item?.rate)}/${item?.unit}`),
            orderCost: createIngDataRenderer((item) => `${formatCurrency(item?.orderQty * item?.rate)} ${item?.unit}`),
            issuedCost: createIngDataRenderer((item) => `${formatCurrency(item?.fullfilledCost)} ${item?.unit}`),
            variance: (product) => (
                <div className='flex flex-col gap-2'>
                    {product?.ingData?.map((item, index) => {
                        const orderCost = Number((item?.orderQty * item?.rate).toFixed(2));
                        const diffOrderedCostFullfilledCost = Number(Math.abs((item?.orderQty * item?.rate) - item?.fullfilledCost).toFixed(0));

                        return orderCost > item?.fullfilledCost ? (
                            <Tooltip title='With in the threshold (±5%)'>
                                <span key={index} className='flex items-center gap-1 text-[12px] text-green-500'>
                                    (<ArrowDown size={12} className='text-green-500' />{diffOrderedCostFullfilledCost})
                                </span>
                            </Tooltip>
                        ) : (
                            <Tooltip title='Outside the threshold (±5%)'>
                                <span key={index} className='flex items-center gap-1 text-[12px] text-red-500'>
                                    (<ArrowUp size={12} className='text-red-500' />{diffOrderedCostFullfilledCost})
                                </span>
                            </Tooltip>
                        );
                    })}
                </div>
            ),
            view: (product) => (
                <div className='flex flex-col gap-2'>
                    {product?.ingData?.map((item, index) => (
                        <Eye
                            key={index}
                            className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125'
                            onClick={() => window.open(
                                `${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${encodeURIComponent(item?.recipeName)}&mealType=${meal}&dateRange=${encodeURIComponent(
                                    JSON.stringify(updatedFilters?.dateRange)
                                )}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({id: -999}))}`
                            )}
                        />
                    ))}
                </div>
            ),
        };

        return {
            data: generatedData(productData?.list || [], customRowData),
            tableRows: customRowData,
            tableColumns: COST_BASED_COLUMNS,
        };
    }, [productData?.list, generatedData, createRowRenderer, createIngDataRenderer, meal, filters]);

    const getTableInfo = useCallback(() => {
        return uiState.selectedTab === 'weight' ? getWeightBasedTable : getCostBasedTable;
    }, [uiState.selectedTab, getWeightBasedTable, getCostBasedTable]);

    const getTabInfo = useMemo(() => ({
        ...TABLE_TAB_CONFIG,
        tabValue: uiState.tableTab,
        handleTabChange: (value) => updateUiState({ tableTab: value }),
    }), [uiState.tableTab, updateUiState]);

    const filteredTabs = useMemo(() => {
        return tabs?.filter((item) => item?.id !== 'percentage');
    }, []);

    const getSortType = (columnName, sortType) => {
        if (columnName) {
            const name = uiState.selectedTab === 'weight' ? SORT_OPTIONS.find(option => option.value === columnName)?.label : SORT_COST_OPTIONS.find(option => option.value === columnName)?.label;
            const sortTypeIcon = sortType === 'ascending' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
            return <span className='flex items-start gap-1'> <strong className='flex items-center gap-1'> <ArrowUpDown size={16} /> Sort By:</strong> {name} {sortTypeIcon}</span>;
        }
        return <span className='flex items-start gap-1'> <ArrowUpDown size={16} /> Sort By</span>;
    };

    return (
        <>
            <GrnHeader
                title='Back to Operations Summary'
                url={backUrl}
                navigateLabel='Product Name'
                id={name}
                navigatePrevToolTip='Prev Product'
                navigateNextToolTip='next Product'
                navigateQC={navigateQc}
                listData={list}
                idx={idx}
            />
            <div className='relative top-[3rem] p-4'>
                <Info
                    buttons={buttons}
                    fieldMap={FIELD_MAP}
                    data={productInfo}
                    heading={{
                        key: name,
                    }}
                />
                <div className='flex justify-between items-center'>
                    <div className='w-fit inline-flex rounded-lg bg-[#FDE6AD] p-1 m-[12px]'>
                        {filteredTabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => updateUiState({ selectedTab: tab.id, sortVal: {} })}
                                className={`px-4 py-2 flex items-center text-sm font-medium transition-all duration-200 rounded-md ${uiState.selectedTab === tab.id
                                    ? 'bg-yellow-400 text-black shadow-sm'
                                    : 'text-gray-700 hover:bg-yellow-300/50'
                                    }`}
                            >
                                <span className='mr-2'>{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className='flex gap-2 items-center'>
                        <InputGroup
                            inside
                            className='rounded-full !bg-gray-200 border border-gray-200 px-2 py-1 !w-[350px]'
                        >
                            <InputGroup.Button
                                className='!bg-transparent !shadow-none pointer-events-none'
                                style={{
                                    background: 'transparent',
                                    boxShadow: 'none',
                                    cursor: 'default',
                                }}
                            >
                                <SearchIcon size={16} className='text-gray-500' />
                            </InputGroup.Button>
                            <Input
                                placeholder='Search by Ingredient or Variant ID'
                                className='bg-transparent border-none focus:outline-none'
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                }}
                                value={uiState.search}
                                onChange={(value) => updateUiState({ search: value })}
                            />
                        </InputGroup>
                        <div className='relative'>
                            <button
                                onClick={() => updateUiState({ isSortOpen: true })}
                                className='px-4 py-2 flex items-center text-sm font-medium transition-all duration-200 rounded-md bg-yellow-400 text-black shadow-sm'
                            >
                                {getSortType(uiState?.sortVal?.sortColumnName, uiState?.sortVal?.sortType)}
                            </button>
                            <SortComp
                                isOpen={uiState.isSortOpen}
                                setIsOpen={(isOpen) => updateUiState({ isSortOpen: isOpen })}
                                onSortChange={handleSortChange}
                                sortOptions={uiState.selectedTab === 'weight' ? SORT_OPTIONS : SORT_COST_OPTIONS}
                            />
                        </div>
                    </div>
                </div>
                <TableWithTabs
                    tableInfo={getTableInfo()}
                    tabInfo={getTabInfo}
                />
            </div>
        </>
    );
}

export default React.memo(IssuedDetailComp);
